<template>
  <div>
    <!-- Botão flutuante -->
    <button class="chat-fab" :class="{ 'chat-fab-notify': hasNotificacoes && !visivel }" @click="toggleChat" ref="botaoChat">
        <img src="../../assets/img/chat.png" alt="Abrir chat" />
    </button>

    <!-- OVERLAY (fundo escurecido) -->
    <div v-if="visivel" class="chat-overlay" @click="fecharChat"></div>

    <!-- Painel de chat visível -->
    <div class="chat-container" v-if="visivel" v-click-outside="fecharChat">
      <div class="card w-100 h-100 shadow-sm">
        <div class="row g-0 h-100 chat-content">
          <!-- Sidebar -->
          <div class="col-4 bg-light border-end rounded-start chat-sidebar">
            <SalaSelector style="height:100%; min-height:0; display:flex; flex-direction:column;"
              :usuario-id="usuarioId"
              :salas="salas"
              :sala-selecionada="salaSelecionada" 
              @salaSelecionada="selecionarSala"
              @criarSala="mostrarCriarSala = true"
              @iniciarConversa="mostrarIniciarConversa = true"
              @editarSala="abrirModalEditarSala"
              @excluirSala="abrirConfirmacaoExclusao"
            />
          </div>

          <!-- Painel de mensagens -->
          <div class="col-8 p-3 d-flex flex-column">
            <ChatRoom
              v-if="salaSelecionada"
              :sala-id="salaSelecionada"
              :usuario-id="usuarioId"
              :usuario-nome="usuarioNome"
              class="flex-grow-1"
              @editarSala="abrirModalEditarSala"
            />
            <div v-else class="chat-placeholder text-muted text-center flex-grow-1 d-flex align-items-center justify-content-center">
              <p class="fst-italic">{{ $t('chatApp.selecionarOuIniciar') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modais -->
    <CriarSalaModal
      v-if="mostrarCriarSala"
      @fechar="mostrarCriarSala = false"
      @salaCriada="novaSalaCriada"
    />
    <!-- Editar Sala -->
    <CriarSalaModal
      v-if="mostrarEditarSala"
      :modo-edicao="true"
      :sala="salaEmEdicao"
      @fechar="mostrarEditarSala = false"
      @salaAtualizada="atualizarSala"
    />
    <IniciarConversaModal
      v-if="mostrarIniciarConversa"
      :conversas-existentes="salas"
      @fechar="mostrarIniciarConversa = false"
      @novaSalaCriada="novaSalaCriada"
    />

    <!-- Confirmação de exclusão (WaveUI) -->
    <w-dialog v-model="confirmDelete.show" :title="$t('chatApp.confirmacaoExclusaoTitulo')" persistent :width="520" class="dialog-top">
      <div class="mb2">
        {{ $t('chatApp.confirmacaoExclusaoMensagem') }}
      </div>

      <template #actions>
        <div class="spacer" />
        <w-button class="mr2" :disabled="confirmDelete.loading" @click="confirmDelete.show = false" bg-color="error">
          {{ $t('chatApp.cancelar') }}
        </w-button>
        <w-button :loading="confirmDelete.loading" :disabled="confirmDelete.loading" @click="confirmarExclusaoSala" bg-color="success">
          {{ $t('chatApp.excluir') }}
        </w-button>
      </template>

      <div v-if="confirmDelete.error" class="mt2" style="color:#c0392b; font-size: 0.9rem;">
        {{ confirmDelete.error }}
      </div>
    </w-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import SockJS from 'sockjs-client'
import { Stomp } from '@stomp/stompjs'
import SalaSelector from './SalaSelector.vue'
import ChatRoom from './ChatRoom.vue'
import CriarSalaModal from './CriarSalaModal.vue'
import IniciarConversaModal from './IniciarConversaModal.vue'
import { useUserStore } from '@/utils/stores/userStore'
import { parseUTCString, nowUTCString } from '@/utils/helpers'

export default {
  name: 'ChatApp',
  
  components: {
    SalaSelector,
    ChatRoom,
    CriarSalaModal,
    IniciarConversaModal
  },
  data() {
    return {
      salas: [],
      salaSelecionada: null,
      salaEmEdicao: null,    // objeto da sala completa
      usuarioId: null,
      usuarioNome: null,
      token: '',
      visivel: false,
      mostrarCriarSala: false,
      mostrarEditarSala: false,
      mostrarIniciarConversa: false,
      stompClientNotificacoes: null, // nova conexão WebSocket privada
      notifSubscription: null,
      pinnedSalaId: null,
      confirmDelete: {
      show: false,
        sala: null,
        loading: false,
        error: ''
      },
    }
  },

  watch: {
    visivel(novoValor) {
      if (novoValor === true) {
        this.carregarSalas();
      }
    }
  },

  computed: {
    hasNotificacoes() {
      return this.salas?.some?.(s => s.temMensagemNova);
    }
  },

  mounted() {
    const userStore = useUserStore()
    this.usuarioId = userStore.id
    this.usuarioNome = userStore.nome
    this.token = userStore.token
    
    this.carregarSalas();
    this.conectarNotificacoes(); // ← conecta ao canal privado
  },
  methods: {
    ordenarSalas(lista) {
      lista.sort((a, b) => {
        // 1) salas com nova mensagem em cima
        if (!!a.temMensagemNova !== !!b.temMensagemNova) {
          return a.temMensagemNova ? -1 : 1;
        }

        // 2) depois por data da última mensagem (mais recente primeiro)
        const da = parseUTCString(a.ultimaMensagem);
        const db = parseUTCString(b.ultimaMensagem);

        if (da && db) return db - da;
        if (da) return -1;
        if (db) return 1;

        // 3) depois por nome
        return (a.nome || '').localeCompare(b.nome || '');
      });

      // Se existir uma sala fixada, garante que ela fique no topo
      if (this.pinnedSalaId != null) {
        const idx = lista.findIndex(s => s && s.id === this.pinnedSalaId);
        if (idx > 0) {
          const [pinned] = lista.splice(idx, 1);
          lista.unshift(pinned);
        }
      }
    },

    async carregarSalas() {
      try {
        const response = await axios.get(
          `${this.$dominio}/api/colaboradores/${this.usuarioId}/chats`,
          {
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          }
        );

        const novas = response.data.chats || [];

        novas.forEach(nova => {
          const antiga = this.salas.find(s => s.id === nova.id);

          // Verifica se ainda sou membro ativo desta sala
          const souMembroAtivo = this.ehMembroAtivo(nova);
          nova.souMembroAtivo = souMembroAtivo;

          // Assinante correspondente ao usuário atual (se ainda estiver na sala)
          const assinanteAtual = souMembroAtivo
            ? nova.assinantes?.find(a =>
                a.assinanteURI?.endsWith(`/${this.usuarioId}`)
              )
            : null;

          const ultimaMsg = parseUTCString
            ? parseUTCString(nova.ultimaMensagem)
            : new Date(nova.ultimaMensagem);

          const ultimaVis = assinanteAtual?.ultimaVisualizacao
            ? (parseUTCString
                ? parseUTCString(assinanteAtual.ultimaVisualizacao)
                : new Date(assinanteAtual.ultimaVisualizacao)
              )
            : null;

          let temNoServidor = false;

          if (souMembroAtivo) {
            // Regra vinda do servidor:
            // tem mensagem nova se existe últimaMensagem
            // e (nunca viu OU últimaMensagem > últimaVisualizacao)
            temNoServidor =
              !!ultimaMsg && (!ultimaVis || ultimaMsg > ultimaVis);
          } else {
            // Ex-membro: nunca mostra badge
            temNoServidor = false;
          }

          // Mantém o que já estava marcado na memória (WebSocket)
          const temLocal = souMembroAtivo && !!antiga?.temMensagemNova;

          nova.temMensagemNova = temNoServidor || temLocal;

          // Se o chat está aberto **e** essa sala está selecionada,
          // consideramos que o usuário já está vendo as mensagens agora.
          if (this.visivel && this.salaSelecionada === nova.id) {
            nova.temMensagemNova = false;
          }
        });

        this.ordenarSalas(novas);

        this.salas = novas;
      } catch (error) {
        console.error('Erro ao carregar salas:', error);
      }
    },

    ehMembroAtivo(sala) {
      return Array.isArray(sala?.assinantes) &&
        sala.assinantes.some(a => a.assinanteURI?.endsWith(`/${this.usuarioId}`));
    },

    async atualizarUltimaVisualizacao(chatId) {
      if (!chatId) return;

      try {
        await axios.patch(
          `${this.$dominio}/api/chats/${chatId}/ultimavisualizacao`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (err) {
        console.warn('Falha ao atualizar ultimaVisualizacao da sala', chatId, err);
      }
    },

    selecionarSala(id) {
      const novaSalaId = Number(id);
      const salaAnteriorId = this.salaSelecionada;

      // 1) Se eu estava em outra sala antes, considero que terminei de vê-la
      if (salaAnteriorId && salaAnteriorId !== novaSalaId) {
        this.atualizarUltimaVisualizacao(salaAnteriorId);

        // Se a sala anterior era a fixada, desfixa
        if (this.pinnedSalaId === salaAnteriorId) {
          this.pinnedSalaId = null;
          this.ordenarSalas(this.salas);
        }
      }

      // 2) Agora, seleciono a nova sala
      this.salaSelecionada = novaSalaId;

      // 3) Limpamos a notificação da nova sala na UI
      const sala = this.salas.find(s => s.id === this.salaSelecionada);
      if (sala) sala.temMensagemNova = false;
    },

    toggleChat() {
      if (this.visivel) {
        // estava aberto -> vamos fechar
        this.fecharChat()
      } else {
        // estava fechado -> vamos abrir
        this.visivel = true
        // se quiser garantir recarga imediata aqui, pode:
        // this.carregarSalas()
      }
    },

    fecharChat() {
      // se estava com alguma sala aberta, registra a última visualização dela
      if (this.salaSelecionada) {
        this.atualizarUltimaVisualizacao(this.salaSelecionada);

        // se a sala selecionada estava fixada, desfixa
        if (this.pinnedSalaId === this.salaSelecionada) {
          this.pinnedSalaId = null;
          this.ordenarSalas(this.salas);
        }
      }

      this.visivel = false;
      this.salaSelecionada = null;
    },

    moverSalaParaTopo(salaId) {
      const idx = this.salas.findIndex(s => s && s.id === salaId);
      if (idx <= 0) return; // já é a primeira ou não achou

      const [sala] = this.salas.splice(idx, 1);
      this.salas.unshift(sala);
    },

    novaSalaCriada(novaSala) {
      const jaExiste = this.salas.some(s => s.id === novaSala.id);

      const ultimaMensagem = novaSala.ultimaMensagem || nowUTCString();

      // fixa a sala recém-criada no topo
      this.pinnedSalaId = novaSala.id;

      if (!jaExiste) {
        this.salas.unshift({
          ...novaSala,
          souMembroAtivo: true,      // você acabou de criar / entrar
          temMensagemNova: false,    // nenhuma pendência
          ultimaMensagem             // segue o mesmo padrão de toda a app
        });
      } else {
        // Já existe: só move a sala existente para o topo e atualiza alguns campos
        this.moverSalaParaTopo(novaSala.id);
        const sala = this.salas[0];
        sala.souMembroAtivo = true;
        sala.temMensagemNova = false;
        sala.ultimaMensagem = ultimaMensagem;
      }

      // Se eu estava em outra sala, considero que terminei de vê-la
      if (this.salaSelecionada && this.salaSelecionada !== novaSala.id) {
        this.atualizarUltimaVisualizacao(this.salaSelecionada);
      }

      this.salaSelecionada = novaSala.id;
      this.visivel = true;

      // Garante que tudo fique ordenado respeitando o "pin"
      this.ordenarSalas(this.salas);
    },

    abrirModalEditarSala(sala) {
      this.salaEmEdicao = sala;
      this.mostrarEditarSala = true;
    },

    atualizarSala(sala) {
      const index = this.salas.findIndex(s => s.id === sala.id);
      if (index !== -1) {
        if (sala.saiuUsuarioAtual) {
          // Mantém o nome e dados anteriores
          this.salas[index] = {
            ...this.salas[index],
            saiuUsuarioAtual: true
          };
        } else {
          // Atualização normal
          this.salas[index] = sala;
        }
      }
    },

    abrirConfirmacaoExclusao(sala) {
      if (!sala?.id) return;
      this.confirmDelete.sala = sala;
      this.confirmDelete.error = '';
      this.confirmDelete.loading = false;
      this.confirmDelete.show = true;
    },

    async confirmarExclusaoSala() {
      const sala = this.confirmDelete.sala;
      if (!sala?.id) return;

      this.confirmDelete.loading = true;
      this.confirmDelete.error = '';

      try {
        await axios.delete(`${this.$dominio}/api/chats/${sala.id}`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        // remove da lista local
        this.salas = this.salas.filter(s => s.id !== sala.id);

        // limpa seleção/modal de edição, se necessário
        if (this.salaSelecionada === sala.id) this.salaSelecionada = null;
        if (this.mostrarEditarSala && this.salaEmEdicao?.id === sala.id) {
          this.mostrarEditarSala = false;
          this.salaEmEdicao = null;
        }

        this.confirmDelete.show = false;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.confirmDelete.error = this.$t('chatApp.naoTemPermissao');
        } else {
          this.confirmDelete.error = this.$t('chatApp.erroExcluir');
        }
        console.error('Erro ao excluir sala:', err);
      } finally {
        this.confirmDelete.loading = false;
      }
    },

    conectarNotificacoes() {
      if (this.stompClientNotificacoes?.connected) return;

      const token = useUserStore().token;

      // const socket = new SockJS(
      //   `${this.$dominio}/chatws?Authorization=${encodeURIComponent(`Bearer ${token}`)}`
      // );
      const socket = new SockJS(`${this.$dominio}/chatws`, null, {
        transports: ["xhr-streaming", "xhr-polling"],
      });
      
      const client = Stomp.over(() => socket);

      // estabilidade
      client.reconnectDelay = 5000;
      client.heartbeatIncoming = 10000;
      client.heartbeatOutgoing = 10000;

      // silencia em produção
      client.debug = () => {};

      client.connect(
        { Authorization: `Bearer ${token}` },
        () => {
          if (this.notifSubscription) {
            try { this.notifSubscription.unsubscribe(); } catch (_) {}
          }
          this.notifSubscription = client.subscribe(
            '/user/queue/notificacoes',
            (frame) => {
              const not = JSON.parse(frame.body);
              if (not.evento === 'newRoom') {
                const salaId = not.salaId;
                const existe = this.salas?.some?.(s => s.id === salaId);
                if (!existe) this.carregarSalas();
              }
              if (not.evento === 'newMessage') {
                const salaId = not.salaId;
                const sala = this.salas?.find?.(s => s.id === salaId);

                if (!sala) {
                  this.carregarSalas();
                  return;
                }

                // se não é mais membro ativo, ignora notificação
                if (!this.ehMembroAtivo(sala)) {
                  sala.temMensagemNova = false; // garante que não fique travado em true
                  return;
                }

                // ATUALIZA A DATA DA ÚLTIMA MENSAGEM
                // usa o que vier da notificação, se existir, senão "agora" em UTC
                sala.ultimaMensagem = not.ultimaMensagem || nowUTCString();

                // Se o chat estiver fechado OU se estiver aberto mas em outra sala,
                // marca como nova mensagem.
                if (!this.visivel || this.salaSelecionada !== salaId) {
                  sala.temMensagemNova = true;
                } else {
                  // se estou na própria sala, considero “lida”
                  sala.temMensagemNova = false;
                }

                this.ordenarSalas(this.salas);
              }
              if (not.evento === 'roomUpdated') {
                const sala = this.salas?.find?.(s => s.id === not.salaId);
                if (sala) sala.nome = not.nome; else this.carregarSalas();
              }
              if (not.evento === "roomMembersChanged") {
                const salaId = not.salaId;
                const token  = this.token; // ou use useUserStore().token, como preferir

                const idx = this.salas.findIndex(s => s && s.id === salaId);
                const antigo = idx !== -1 ? { ...this.salas[idx] } : null;

                axios.get(`${this.$dominio}/api/chats/${salaId}`, {
                  headers: { Authorization: `Bearer ${token}` }
                }).then(res => {
                  const salaAtualizada = res.data || {};
                  // Nunca substitua "no seco". Mescle com o objeto antigo
                  //  para garantir que id/nome antigos não sumam no meio do caminho.
                  const mesclada = { ...(antigo || {}), ...salaAtualizada, id: salaId };

                  // recalcula se é membro ativo
                  mesclada.souMembroAtivo = this.ehMembroAtivo(mesclada);
                  if (!mesclada.souMembroAtivo) {
                    mesclada.temMensagemNova = false; // ex-membros não têm badge
                  }

                  if (idx !== -1) {
                    this.salas.splice(idx, 1, mesclada);
                  } else {
                    this.salas.push(mesclada);
                  }

                  // Se modal de edição estiver aberto desta sala, mantém em sincronia
                  if (this.mostrarEditarSala && this.salaEmEdicao?.id === salaId) {
                    this.salaEmEdicao = mesclada;
                  }
                }).catch(err => {
                  // Falhou (ex.: timing/race). NÃO substitua o item!
                  // Preserve o “antigo” para não virar “Sala #–”.
                  console.warn('[NOTIF] Falha ao atualizar sala (possível remoção):', err?.response?.status);
                  if (idx !== -1 && this.salas[idx]) {
                    this.salas[idx].removidaParaMim = true; // só marca p/ UI, se quiser
                  }
                });
              }
            },
            { Authorization: `Bearer ${token}` }
          );
        }
      );

      this.stompClientNotificacoes = client;
    }
  },
  //para fechar o chat ao clicar fora
  directives: {
    clickOutside: {
      beforeMount(el, binding) {
        const vm = binding.instance;            // 👈 componente ChatApp
        const botaoChat = vm?.$refs?.botaoChat;

        el.clickOutsideEvent = (event) => {
          // 1) Se há qualquer modal aberto, NÃO fecha
          if (
            vm?.mostrarCriarSala ||
            vm?.mostrarEditarSala ||
            vm?.mostrarIniciarConversa ||
            vm?.confirmDelete?.show
          ) {
            return;
          }
          const dentroDoWaveDialog = !!event.target.closest('.w-dialog__container, .w-overlay');
          if (dentroDoWaveDialog) return;

          // 2) Ignora cliques dentro do chat ou no botão flutuante
          if (
            el.contains(event.target) ||
            (botaoChat && botaoChat.contains(event.target))
          ) {
            return;
          }

          // Fecha
          binding.value(event);
        };

        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  },
}
</script>

<style scoped>
.chat-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  background-color: var(--sec-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,.3);
  display: inline-flex;           /* centraliza o conteúdo */
  align-items: center;
  justify-content: center;
  padding: 0;                     /* garante círculo perfeito */
}

.chat-fab img {
  width: 26px;                    /* ajuste fino do tamanho do ícone */
  height: 26px;
  display: block;
  pointer-events: none;           /* clique “fura” até o botão */
}

.chat-fab-notify {
  background-color: var(--notificacao-verde) !important;
  animation: chat-pulse 1.3s infinite ease-in-out;
  transform-origin: center;
}

@keyframes chat-pulse {
  0%   { transform: scale(1); }
  50%  { transform: scale(1.12); }
  100% { transform: scale(1); }
}

.chat-container {
  position: fixed;
  bottom: 90px;
  right: 24px;
  width: 800px;
  height: clamp(460px, 70vh, 80dvh); /* adapta a várias resoluções */
  max-height: 90vh;
  z-index: 1000;

  /* ESSENCIAL: o scroll deve ocorrer nos filhos, não aqui */
  overflow: hidden;
  display: flex; /* para o .card ocupar 100% */
}

/* o card ocupa 100% da altura da caixa do chat */
.chat-container > .card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0; /* ESSENCIAL */
}

/* a row ocupa toda a altura do card */
.chat-content {
  height: 100%;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

/* cada coluna precisa poder encolher verticalmente E ter altura 100% */
.chat-content > [class*="col-"] {
  height: 100%;        /* <<< ADICIONE */
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* a coluna da esquerda também com 100% p/ não “sumir” altura no caminho */
.chat-sidebar {
  height: 100%;        /* <<< ADICIONE */
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* o root do SalaSelector (.sidebar) herda 100% da coluna */
.chat-sidebar :deep(.sidebar) {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;    /* scroll fica só na lista */
}

/* a lista rola de verdade */
.chat-sidebar :deep(.sidebar-scroll) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;    /* <<< a barra aparece aqui */
}

.dialog-top {
  z-index: 9999 !important;
}

.dialog-top + .w-overlay {
  z-index: 9998 !important;
}

/* Evita que o scroll 'vaze' pra página atrás */
.chat-container,
.chat-content,
.chat-sidebar,
.chat-content > [class*="col-"],
.chat-container :deep(.sidebar),
.chat-container :deep(.sidebar-scroll),
.chat-container :deep(.chat-messages) {
  overscroll-behavior: contain;
}

/* Fallback de gesto em navegadores mais antigos (iOS/Safari velho) */
@supports not (overscroll-behavior: contain) {
  .chat-container { touch-action: none; }
  .chat-container :deep(.sidebar-scroll),
  .chat-container :deep(.chat-messages) { touch-action: pan-y; }
}

/* Escurece o fundo quando o chat está aberto */
.chat-overlay {
  position: fixed;
  inset: 0;                           /* top/right/bottom/left: 0 */
  background: rgba(0, 0, 0, 0.35);    /* opacidade do fundo */
  backdrop-filter: blur(2px);         /* opcional: leve blur */
  z-index: 9998;                       /* abaixo do chat e do botão */
}

/* Garanta a ordem de camadas */
.chat-container { z-index: 9999; }     /* acima do overlay */
.chat-fab       { z-index: 99999 !important; }    /* acima do chat (se quiser manter visível) */
</style>